import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { TranslocoService } from "@ngneat/transloco";
import { Navigation } from "app/core/navigation/navigation.types";
import { Observable, ReplaySubject, tap } from "rxjs";
import { UserService } from "app/core/user/user.service";
import { Permission } from "app/core/auth/permissions";
import { FuseNavigationItem } from "@fuse/components/navigation";

@Injectable({ providedIn: "root" })
export class NavigationService {
  private _httpClient = inject(HttpClient);
  private _navigation: ReplaySubject<Navigation> = new ReplaySubject<Navigation>(1);
  private translocoService = inject(TranslocoService);
  private _userService = inject(UserService);

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for navigation
   */
  get navigation$(): Observable<Navigation> {
    return this._navigation.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Observable<Navigation> {
    return this._httpClient.get<Navigation>("assets/core/navigation.json").pipe(
      tap(navigation => {
        // Translate the navigation titles
        this.translateNavigationTitles(navigation);

        // Filter navigation by permission
        this.filterNavigationByPermission(navigation);

        this._navigation.next(navigation);
      })
    );
  }

  private translateNavigationTitles(navigation: Navigation): void {
    const translateItem = (item: any) => {
      item.title = this.translocoService.translate(item.title);
    };

    Object.keys(navigation).forEach(key => {
      navigation[key].forEach(translateItem);
    });
  }

  // Guest List for menu permissions, Add new menu IDs and their required permission keys here
  private readonly _menuPermissions: Record<string, string> = {
    precast: Permission.viewConcretePrecastPermission,
    "concrete-reporting": Permission.runConcreteReportingPermission,
    countries: Permission.globalAdmin,
    "road-state-authorities": Permission.globalAdmin,
    "cement-client-sources": Permission.viewCementClientSourcePermission,
    "custom-worksheet-templates": Permission.manageCustomWorksheetTemplatesPermission,
    clients: Permission.manageClientsPermission,
    projects: Permission.manageProjectsPermission,
    equipment: Permission.manageEquipmentsPermission,
    "test-providers": Permission.manageTestProvidersPermission,
    specifications: Permission.manageSpecificationsPermission,
  };

  // Filter navigation items by permission
  private filterNavigationByPermission(navigation: Navigation): void {
    const filterItem = (item: FuseNavigationItem) => {
      // Check if this item is on our Guest List
      const requiredPermission = this._menuPermissions[item.id];

      if (requiredPermission) {
        item.hidden = () => !this._userService.hasPermission(requiredPermission);
      }

      // Recursively filter children if any
      if (item.children) {
        item.children.forEach(filterItem);
      }
    };

    Object.keys(navigation).forEach(key => {
      navigation[key].forEach(filterItem);
    });
  }
}
